/**
 * Template Field Placement Types
 *
 * Defines the data model for drag-and-drop field mapping on PDF templates.
 * Users place claim metadata fields onto specific positions on the PDF.
 * When generating documents per claim, these positions are filled with real data.
 */

// ── Field Placement (stored per-template in the DB) ──

/** Field type determines how the field is handled */
export type FieldType = 'data' | 'text_input' | 'signature' | 'static_text' | 'adjuster_signature';

/** A single field placed on a PDF template page. Coordinates are percentages (0–100) of page dimensions. */
export interface FieldPlacement {
  id: string;
  fieldKey: string;
  label: string;
  type: FieldType;
  page: number;
  x: number;
  y: number;
  width: number;
  height: number;
  fontSize: number;
  fontFamily: string;
  textAlign: 'left' | 'center' | 'right';
  required?: boolean; // For text_input and signature fields
  /** Whether user-entered text wraps across multiple lines (true) or stays on a single shrink-to-fit line (false). */
  multiline?: boolean;
  /** Static content authored by the adjuster — used for static_text and adjuster_signature fields */
  staticContent?: string;
}

// ── Claim Field Definitions (drives the field palette in the editor) ──

/** Describes a single claim metadata field that can be placed on a template. */
export interface ClaimFieldDefinition {
  fieldKey: string;
  label: string;
  category: string;
  type: FieldType;
  /** Dot-notation path into the claim/metadata object */
  dataPath?: string;
  /** Example value shown in the editor preview */
  exampleValue?: string;
  /** Whether user-entered text wraps across multiple lines. */
  multiline?: boolean;
  staticContent?: string;
}

/**
 * Master list of all available claim fields.
 * This drives the field palette in the template editor.
 * Must be kept in sync with the backend resolveFieldValue() mapping.
 */
export const CLAIM_FIELD_DEFINITIONS: ClaimFieldDefinition[] = [
  // Insured / Homeowner
  { fieldKey: 'insured_name', label: 'Insured Name', category: 'Insured', type: 'data', dataPath: 'insuredName', exampleValue: 'John Doe' },
  { fieldKey: 'mailing_address', label: 'Mailing Address', category: 'Insured', type: 'data', dataPath: 'mailingAddress', exampleValue: 'PO Box 123, City, ST 12345' },
  { fieldKey: 'insured_phone', label: 'Insured Phone', category: 'Insured', type: 'data', dataPath: 'insuredPhone', exampleValue: '(555) 123-4567' },
  { fieldKey: 'insured_phone2', label: 'Insured Phone 2', category: 'Insured', type: 'data', dataPath: 'insuredPhone2', exampleValue: '(555) 987-6543' },
  { fieldKey: 'insured_email', label: 'Insured Email', category: 'Insured', type: 'data', dataPath: 'insuredEmail', exampleValue: 'john.doe@email.com' },
  { fieldKey: 'insured_email2', label: 'Insured Email 2', category: 'Insured', type: 'data', dataPath: 'insuredEmail2', exampleValue: 'john.doe2@email.com' },

  // Claim
  { fieldKey: 'insured_address', label: 'Loss Address', category: 'Claim', type: 'data', dataPath: 'propertyAddress', exampleValue: '123 Main St, City, ST 12345' },
  { fieldKey: 'date_of_loss', label: 'Date of Loss', category: 'Claim', type: 'data', dataPath: 'dateOfLoss', exampleValue: '02/15/2026' },
  { fieldKey: 'type_of_loss', label: 'Cause of Loss', category: 'Claim', type: 'data', dataPath: 'lossType', exampleValue: 'Wind/Hail' },
  { fieldKey: 'claim_number', label: 'Claim Number', category: 'Claim', type: 'data', dataPath: 'claimNumber', exampleValue: 'CLM-2026-00001' },
  { fieldKey: 'insurance_carrier', label: 'Insurance Company', category: 'Claim', type: 'data', dataPath: 'insuranceCarrier', exampleValue: 'State Farm' },
  { fieldKey: 'policy_number', label: 'Policy Number', category: 'Claim', type: 'data', dataPath: 'policyNumber', exampleValue: 'POL-987654' },
  { fieldKey: 'contractor', label: 'Contractor', category: 'Claim', type: 'data', dataPath: 'contractor', exampleValue: 'ABC Restoration' },

  // System
  { fieldKey: 'current_date', label: 'Current Date', category: 'System', type: 'data', dataPath: '_system.current_date', exampleValue: '03/17/2026' },
];

/**
 * Group field definitions by category for the palette UI.
 */
export function getFieldsByCategory(): Record<string, ClaimFieldDefinition[]> {
  return CLAIM_FIELD_DEFINITIONS.reduce<Record<string, ClaimFieldDefinition[]>>((acc, field) => {
    if (!acc[field.category]) {
      acc[field.category] = [];
    }
    acc[field.category].push(field);
    return acc;
  }, {});
}

/**
 * Create a default placement when a field is first dropped onto the PDF.
 */
export function createDefaultPlacement(
  fieldDef: ClaimFieldDefinition,
  page: number,
  x: number,
  y: number,
): FieldPlacement {
  const isSignatureType = fieldDef.type === 'signature' || fieldDef.type === 'adjuster_signature';
  const isLongTextField = fieldDef.fieldKey.startsWith('homeowner_long_text_');
  const isCheckboxField = fieldDef.fieldKey.startsWith('homeowner_checkbox_');
  const width = isSignatureType ? 30 : isCheckboxField ? 4 : (fieldDef.type === 'static_text' || isLongTextField ? 40 : 20);
  const height = isSignatureType ? 8 : isCheckboxField ? 3 : (fieldDef.type === 'static_text' ? 5 : (isLongTextField ? 15 : 3));

  // Multiline defaults on for long-text fields; otherwise honor the definition.
  const multiline = isLongTextField ? true : fieldDef.multiline;

  // Clamp so the field stays fully within page bounds (0–100%)
  const clampedX = Math.max(0, Math.min(x, 100 - width));
  const clampedY = Math.max(0, Math.min(y, 100 - height));

  return {
    id: crypto.randomUUID(),
    fieldKey: fieldDef.fieldKey,
    label: fieldDef.label,
    type: fieldDef.type,
    page,
    x: clampedX,
    y: clampedY,
    width,
    height,
    fontSize: fieldDef.type === 'static_text' ? 10 : 10,
    fontFamily: 'Helvetica',
    textAlign: isCheckboxField ? 'center' : 'left',
    required: !isCheckboxField && (fieldDef.type === 'text_input' || fieldDef.type === 'signature'),
    ...(multiline !== undefined ? { multiline } : {}),
    ...(fieldDef.type === 'static_text' ? { staticContent: fieldDef.staticContent ?? '' } : {}),
    ...(fieldDef.type === 'adjuster_signature' ? { staticContent: fieldDef.staticContent ?? '' } : {}),
  };
}

/** Create a custom text input field with a given label */
export function createCustomTextInputField(label: string): ClaimFieldDefinition {
  return {
    fieldKey: `custom_${crypto.randomUUID()}`,
    label,
    category: 'Custom Fields',
    type: 'text_input',
  };
}

/** Create a two-letter homeowner initials field. */
export function createInitialsField(): ClaimFieldDefinition {
  return {
    fieldKey: `homeowner_initials_${crypto.randomUUID()}`,
    label: 'Initials',
    category: 'Homeowner Fields',
    type: 'text_input',
  };
}

/** Create a multiline homeowner text field. */
export function createLongTextField(label: string = 'Long Text'): ClaimFieldDefinition {
  return {
    fieldKey: `homeowner_long_text_${crypto.randomUUID()}`,
    label,
    category: 'Homeowner Fields',
    type: 'text_input',
    multiline: true,
  };
}

/** Create a homeowner checkbox field. Stamped as 'X' when checked. */
export function createCheckboxField(label: string = 'Checkbox'): ClaimFieldDefinition {
  return {
    fieldKey: `homeowner_checkbox_${crypto.randomUUID()}`,
    label,
    category: 'Homeowner Fields',
    type: 'text_input',
  };
}

/** Create a numeric homeowner input field. */
export function createNumberField(label: string = 'Number'): ClaimFieldDefinition {
  return {
    fieldKey: `homeowner_number_${crypto.randomUUID()}`,
    label,
    category: 'Homeowner Fields',
    type: 'text_input',
  };
}

/** Create a homeowner date field. */
export function createDateField(label: string = 'Date'): ClaimFieldDefinition {
  return {
    fieldKey: `homeowner_date_${crypto.randomUUID()}`,
    label,
    category: 'Homeowner Fields',
    type: 'text_input',
  };
}

/** Create a signature field */
export function createSignatureField(label: string = 'Signature'): ClaimFieldDefinition {
  return {
    fieldKey: `signature_${crypto.randomUUID()}`,
    label,
    category: 'Custom Fields',
    type: 'signature',
  };
}

/** Create a static text field — adjuster-authored content baked into every generated document */
export function createStaticTextField(label: string = 'Text Block', staticContent = ''): ClaimFieldDefinition {
  return {
    fieldKey: `static_text_${crypto.randomUUID()}`,
    label,
    category: 'Your Content',
    type: 'static_text',
    staticContent,
  };
}

/** Create an adjuster signature field — the adjuster's own signature placed on the template */
export function createAdjusterSignatureField(label: string = 'Your Signature', staticContent = ''): ClaimFieldDefinition {
  return {
    fieldKey: `adjuster_sig_${crypto.randomUUID()}`,
    label,
    category: 'Your Content',
    type: 'adjuster_signature',
    staticContent,
  };
}
